import React from 'react';
import { observer } from 'mobx-react';
import { ConfiguratorUIElementStates } from '@/elements/base/ElementState';
import { ComponentElementState } from '@/elements/ComponentElement/ComponentElementState';
import { Icon, useDisplayContext, useTranslator, useUiLabel } from '@canvas-logic/clp-shared-ui';
import classNames from 'classnames';
import { IUICfgProductsMergeContainer, IUICfgComponentElementOptions } from '@canvas-logic/clp-api-types';
import { Card } from '@/elements/shared/Card/Card';
import Tooltip from '@/ui-kit/components/Tooltip/Tooltip';
import { ContainerLevelContext } from '@/ui-kit/contexts/ContainerLevelContext';
import { gapLevelToCssValue } from '@/elements/shared/gapUtils';
import { ProductLookup } from '@/ui-kit/domain/lookup/ProductLookup';
import { IComponentProductData } from '@canvas-logic/clp-domain';
import { ConfiguratorContext } from '@/components/Configurator/ConfiguratorContext';
import { labelId } from '@canvas-logic/clp-localization';
import { useUiCfgGroupLabel } from '@/localization/useUiCfgGroupLabel';

type MergedProduct = IComponentProductData & {
  _componentKey: string;
  _productLookup: ProductLookup;
  _showSwitcher: boolean;
  _canBeDisable: boolean;
  _componentIsOff: boolean;
};

export const ProductsContainerCfgUIElement = observer(function ProductsContainerCfgUIElement({
  viewElement,
  childrenItems,
}: {
  childrenItems: ConfiguratorUIElementStates[];
  viewElement: IUICfgProductsMergeContainer;
}) {
  const [expanded, setExpanded] = React.useState(!viewElement.expandable || !!viewElement.defaultExpanded);
  const translator = useTranslator();
  const displayCtx = useDisplayContext();
  const ctx = React.useContext(ConfiguratorContext);
  const api = ctx?.sdk?.api as
    | { getComponentProductType?: (componentKey: string) => { key: string; isComposite: boolean } | undefined }
    | undefined;

  // Container level for styling hierarchy (set by virtualLayoutBuilder or mocks)
  const level = (viewElement as any)._level ?? 1;
  // Parent level for title styling (title appears in parent's visual context)
  const parentLevel = (viewElement as any)._parentLevel ?? level;
  const titleLevel =
    viewElement.titleSize && viewElement.titleSize !== 'auto'
      ? parseInt(viewElement.titleSize.replace('level-', ''), 10)
      : parentLevel;

  const productSettings = viewElement.productsSettings!;
  const productsEnabled = productSettings.enabled !== false;
  const layout = productSettings.layout ?? 'list';
  const expandable = viewElement.expandable;
  // ADR-0045: resolve translations for the merge-container's group title /
  // description. Falls back to the live system value when no override matches.
  const translatedTitle = useUiCfgGroupLabel(viewElement.id, 'title', viewElement.title);
  const translatedDescription = useUiCfgGroupLabel(viewElement.id, 'desc', viewElement.description);
  const alwaysOnHint = useUiLabel('ui.card.alwaysOnHint');
  const showTitle = viewElement.showTitle && translatedTitle;
  const showDescription = viewElement.showDescription && translatedDescription;

  // Display settings with defaults
  const showImage = productSettings.showImage ?? false;
  const showProductTitle = productSettings.showTitle ?? true;
  const showProductDescription = productSettings.showDescription ?? true;
  const showLongDescription = productSettings.showLongDescription ?? false;

  // Extract and merge products from component children
  // No useMemo — MobX observer tracks productLookup observables accessed here
  const mergedProducts: MergedProduct[] = [];

  childrenItems.forEach((childState) => {
    if (childState instanceof ComponentElementState) {
      const { productLookup, componentKey, viewElement } = childState;
      const componentUiSettings = viewElement.uiControl as IUICfgComponentElementOptions | undefined;
      // CP-1104: under the "2+" rule the component's on/off toggle is hidden for a
      // single-product component, so ignore the switch there — otherwise the lone
      // product can't be selected and there's no toggle to enable it.
      const showComponent = componentUiSettings?.showComponent ?? '2+';
      const productCount = productLookup.productData?.length ?? 0;
      const switcherHidden = showComponent === '2+' && productCount < 2;
      const showSwitcher = (componentUiSettings?.showSwitcher ?? false) && !switcherHidden;
      const componentIsOff = showSwitcher ? !childState.lookup.current?.value : !productLookup.isEnable();

      if (productLookup.initialized && productLookup.productData && (!componentIsOff || showSwitcher)) {
        productLookup.productData.forEach((product) => {
          mergedProducts.push({
            ...product,
            _componentKey: componentKey,
            _productLookup: productLookup,
            _showSwitcher: showSwitcher,
            _canBeDisable: productLookup.canBeDisable,
            _componentIsOff: componentIsOff,
          });
        });
      }
    }
  });

  // Card orientation based on layout
  const cardOrientation = layout === 'card' ? 'vertical' : 'horizontal';
  const lookupLabel = (canonical: string, systemValue: string): string =>
    translator.entity(canonical, displayCtx, systemValue);
  const translateProductName = (componentKey: string, productKey: string, systemValue: string): string => {
    const pt = api?.getComponentProductType?.(componentKey);
    if (!pt) return systemValue;
    return lookupLabel(labelId.productName(pt.isComposite ? 'cpt' : 'bpt', pt.key, productKey), systemValue);
  };
  const translateProductDescription = (componentKey: string, productKey: string, systemValue: string): string => {
    const pt = api?.getComponentProductType?.(componentKey);
    if (!pt) return systemValue;
    return lookupLabel(labelId.productDesc(pt.isComposite ? 'cpt' : 'bpt', pt.key, productKey), systemValue);
  };

  const groupGapCss = gapLevelToCssValue(viewElement.groupGapLevel);
  const afterNameGapCss = gapLevelToCssValue(viewElement.afterNameGap);
  const cardsGapCss = gapLevelToCssValue(productSettings.cardsGap);

  return (
    <ContainerLevelContext.Provider value={level}>
      <div
        className={classNames(
          'cp-product-merge-container',
          'cp-products-container',
          viewElement.expandable && expanded && 'cp-mixed-container--expanded',
          viewElement.expandable && !expanded && 'cp-mixed-container--collapsed',
        )}
        data-element-id={viewElement.id}
        data-container-type="products"
        data-container-level={level}
        key={viewElement.id}
      >
        {(showTitle || showDescription) && (
          <div
            className="cp-mixed-container__header"
            data-title-level={titleLevel}
            style={afterNameGapCss ? ({ '--after-name-gap': afterNameGapCss } as React.CSSProperties) : undefined}
          >
            {showTitle && <h3 className="cp-mixed-container__title">{translatedTitle}</h3>}
            {expandable && (
              <button
                className="cp-mixed-container__expand-button"
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                aria-expanded={expanded}
              >
                <Icon type={expanded ? 'chevronUp' : 'chevronDown'} />
              </button>
            )}
            {showDescription && <p className="cp-mixed-container__description">{translatedDescription}</p>}
          </div>
        )}

        <div className={classNames('cp-expandable-wrapper', !expanded && 'cp-expandable-wrapper--collapsed')}>
          <div className="cp-expandable-wrapper__inner">
            {productsEnabled && (
              <>
                {mergedProducts.length === 0 ? (
                  <div className="cp-products-container__empty">No products available</div>
                ) : (
                  <div
                    className={classNames('cp-products', layout === 'card' && 'cp-products--card')}
                    style={
                      groupGapCss || cardsGapCss
                        ? ({
                            ...(groupGapCss ? { '--container-gap': groupGapCss } : {}),
                            ...(cardsGapCss ? { '--cards-gap': cardsGapCss } : {}),
                          } as React.CSSProperties)
                        : undefined
                    }
                  >
                    {mergedProducts.map((productData) => {
                      const { _productLookup, _showSwitcher, _canBeDisable, _componentIsOff } = productData;
                      const selected = String(productData.productId) === _productLookup.current?.value.value;
                      const canBeDisable = _canBeDisable;
                      const isLocked = !canBeDisable && _productLookup.productData.length === 1;
                      const isDisabledBySwitch = _showSwitcher && _componentIsOff;
                      const hasTooltip = showLongDescription && !!productData.texts.description;

                      return (
                        <div
                          className="cp-product"
                          data-product-id={productData.productId}
                          key={`${productData._componentKey}-${productData.productId}`}
                        >
                          <Tooltip
                            disabled={!hasTooltip}
                            title={''}
                            description={translateProductDescription(
                              productData._componentKey,
                              productData.productKey,
                              productData.texts.description ?? '',
                            )}
                          >
                            {(onOpen) => (
                              <Card
                                orientation={cardOrientation}
                                imageSrc={showImage ? productData.images[0]?.url : undefined}
                                imageFit={productSettings.imageFit}
                                label={
                                  showProductTitle
                                    ? translateProductName(
                                        productData._componentKey,
                                        productData.productKey,
                                        productData.texts.name ?? '',
                                      )
                                    : undefined
                                }
                                description={
                                  showProductDescription
                                    ? translateProductDescription(
                                        productData._componentKey,
                                        productData.productKey,
                                        productData.texts.shortDescription ?? '',
                                      )
                                    : ''
                                }
                                value={productData.productId}
                                selected={selected}
                                disabled={isLocked || isDisabledBySwitch}
                                hasAlwaysOn={isLocked}
                                alwaysOnHint={alwaysOnHint}
                                onSelect={(value) => {
                                  if (_showSwitcher && (_productLookup.productData.length <= 1 || selected)) return;

                                  if (selected) {
                                    if (canBeDisable) {
                                      _productLookup.unselect();
                                    }
                                  } else {
                                    _productLookup.select({ value: String(value) });
                                  }
                                }}
                                onTooltipClick={onOpen}
                                hasTooltip={hasTooltip}
                              />
                            )}
                          </Tooltip>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ContainerLevelContext.Provider>
  );
});
